import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Breadcrumbs,
  Link,
  Alert,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { categoriesService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import IconUpload from '../components/IconUpload';

const CategoryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Déterminer si nous sommes en mode création
  const isNewCategory = location.pathname === '/categories/new';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [icon, setIcon] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    displayOrder: 0,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (!isNewCategory && id) {
        const categoryData = await categoriesService.getById(id);
        if (categoryData && categoryData.data) {
          setFormData({
            name: categoryData.data.name || '',
            description: categoryData.data.description || '',
            displayOrder: categoryData.data.displayOrder || 0,
          });

          // Charger l'icône existante si disponible
          if (categoryData.data.icon) {
            setIcon({
              id: 'existing-icon',
              preview: `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${categoryData.data.icon}`,
              name: 'Icône existante',
              file: null,
              isExisting: true
            });
          }


        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      // Préparer les données avec l'icône
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('displayOrder', formData.displayOrder.toString());
      
      // Ajouter l'icône si elle existe et n'est pas déjà sauvegardée
      if (icon && icon.file && !icon.isExisting) {
        submitData.append('icon', icon.file);
      }

      let categoryId;
      if (isNewCategory) {
        const response = await categoriesService.create(submitData);
        categoryId = response.data.id;
      } else {
        await categoriesService.update(id, submitData);
        categoryId = id;
      }



      navigate('/categories');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de la catégorie');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return <LoadingSpinner message="Chargement de la catégorie..." />;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/categories"
          onClick={(e) => {
            e.preventDefault();
            navigate('/categories');
          }}
        >
          Catégories
        </Link>
        <Typography color="text.primary">
          {isNewCategory ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isNewCategory ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom de la catégorie"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ordre d'affichage"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
                helperText="Un nombre plus petit sera affiché en premier"
              />
            </Grid>
            <Grid item xs={12}>
              <IconUpload
                icon={icon}
                onIconChange={setIcon}
                title="Icône de la catégorie"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/categories')}
                  disabled={saving}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                >
                  {saving ? 'Sauvegarde...' : (isNewCategory ? 'Créer' : 'Modifier')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CategoryEdit;
